import { generateTerrain, generatedMissing, generatedSurface, terrainDataVersion, type MissingTile, type TerrainContainer } from '../terrain/index.ts';
import { generateSpikeTiles } from '../editor/spikes.ts';
import type { BakedLayer, BakedLevel, BakedTile, FoliagePaint, GrassLines, GrassPaint, LevelDoc, PaletteEntry, SpikeLines, TerrainShape, TileLayer } from './format.ts';
import { FORMAT_VERSION } from './format.ts';

interface CacheEntry {
  layers: string;
  version: number;
  grass: GrassPaint | undefined;
  lines: GrassLines | undefined;
  foliage: FoliagePaint | undefined;
  /** generation appends to the palette in place, so indices are only valid for this array */
  palette: PaletteEntry[];
  /** terrain without spikes: painting spikes only redoes the spike layer, not the whole terrain */
  base: Map<string, number[]>;
  spikes: { marks: SpikeLines | undefined; out: Map<string, number[]> } | null;
}

const EMPTY = new Map<string, number[]>();
const NO_SURFACE: number[] = [];
const cache = new WeakMap<TerrainShape[], CacheEntry>();

function terrainEntry(doc: LevelDoc, c: TerrainContainer): CacheEntry | null {
  if (!c.terrains.length) return null;
  const layers = c.layers.map((l) => l.id).join(',');
  const version = terrainDataVersion();
  let e = cache.get(c.terrains);
  if (!e || e.layers !== layers || e.version !== version || e.palette !== doc.palette || e.grass !== c.grassPaint || e.lines !== c.grassLines || e.foliage !== c.decoPaint) {
    e = { layers, version, grass: c.grassPaint, lines: c.grassLines, foliage: c.decoPaint, palette: doc.palette, base: generateTerrain(doc, c), spikes: null };
    cache.set(c.terrains, e);
  }
  if (!e.spikes || e.spikes.marks !== c.spikeLines) {
    const spikes = generateSpikeTiles(doc, c, generatedSurface(e.base));
    let out = e.base;
    if (spikes?.tiles.length) {
      out = new Map(e.base);
      const prev = out.get(spikes.layerId);
      out.set(spikes.layerId, prev?.length ? [...prev, ...spikes.tiles] : spikes.tiles);
    }
    e.spikes = { marks: c.spikeLines, out };
  }
  return e;
}

/** Terrain output cached until the shapes (immutable updates), target layers, palette or style data change. */
export function terrainTiles(doc: LevelDoc, c: TerrainContainer): Map<string, number[]> {
  return terrainEntry(doc, c)?.spikes?.out ?? EMPTY;
}

/** Cells of a container's terrain on slopes its style has no tile for (see MissingTile). */
export function terrainMissing(doc: LevelDoc, c: TerrainContainer): MissingTile[] {
  const e = terrainEntry(doc, c);
  return e ? generatedMissing(e.base) : [];
}

/** Surface cells of a container's terrain: flat [x, y, own shape, exposed LINE_* bits, exposed halves]. */
export function terrainSurface(doc: LevelDoc, c: TerrainContainer): number[] {
  const e = terrainEntry(doc, c);
  return e ? generatedSurface(e.base) : NO_SURFACE;
}

/**
 * Final tiles of a layer: generated terrain tiles overlaid with hand-placed tiles.
 * A hand-placed cell with p = -1 erases the generated tile.
 */
export function effectiveTiles(layer: TileLayer, generated: number[] | undefined): number[] {
  if (!generated || !generated.length) {
    if (!layer.tiles.some((_, i) => i % 4 === 2 && layer.tiles[i] < 0)) return layer.tiles;
  }
  const map = new Map<string, [number, number]>();
  if (generated) for (let i = 0; i < generated.length; i += 4) map.set(`${generated[i]},${generated[i + 1]}`, [generated[i + 2], generated[i + 3]]);
  for (let i = 0; i < layer.tiles.length; i += 4) map.set(`${layer.tiles[i]},${layer.tiles[i + 1]}`, [layer.tiles[i + 2], layer.tiles[i + 3]]);
  const out: number[] = [];
  for (const [k, [p, f]] of map) {
    if (p < 0) continue;
    const c = k.indexOf(',');
    out.push(+k.slice(0, c), +k.slice(c + 1), p, f);
  }
  return out;
}

export function containerTiles(doc: LevelDoc, c: TerrainContainer): Map<string, number[]> {
  const gen = terrainTiles(doc, c);
  const out = new Map<string, number[]>();
  for (const l of c.layers) out.set(l.id, effectiveTiles(l, gen.get(l.id)));
  return out;
}

function bakeLayer(l: TileLayer, tiles: number[]): BakedLayer {
  const { tiles: _drop, ...rest } = l;
  void _drop;
  const cells: BakedTile[] = [];
  for (let i = 0; i < tiles.length; i += 4) cells.push({ x: tiles[i], y: tiles[i + 1], p: tiles[i + 2], f: tiles[i + 3] });
  return { ...rest, cells };
}

/** Export form consumed by the Unity importer: terrain merged into plain tile layers. */
export function bakeLevel(doc: LevelDoc): BakedLevel {
  const top = containerTiles(doc, doc);
  return {
    format: 'platformer-level-baked',
    version: FORMAT_VERSION,
    name: doc.name,
    palette: doc.palette,
    settings: doc.settings,
    layers: doc.layers.map((l) => bakeLayer(l, top.get(l.id) ?? l.tiles)),
    objects: doc.objects,
    bodies: doc.bodies.map((b) => {
      const bt = containerTiles(doc, b);
      const { terrains: _t, grassPaint: _g, grassLines: _gl, spikeLines: _sl, decoPaint: _f, layers, ...rest } = b;
      void _t;
      void _g;
      void _gl;
      void _sl;
      void _f;
      return { ...rest, layers: layers.map((l) => bakeLayer(l, bt.get(l.id) ?? l.tiles)) };
    }),
    system: doc.system,
  };
}
